import type { ReactElement } from "react";
import { useCallback, useMemo, useState } from "react";

import classNames from "classnames";

import { TabSelector } from "./TabSelector/TabSelector";

import type { TabItemProps } from "./TabItem";

import styles from "./Tabs.module.scss";

type TabItem = ReactElement<TabItemProps>;

export type TabsProps = {
  children: TabItem | TabItem[];
  defaultTabIndex?: number;
  onTabClick?: (itemIndex: number) => void;
  tabsWrapperClassName?: string;
  tabMenuClassName?: string;
  tabContentClassName?: string;
};

export const Tabs = ({
  defaultTabIndex = 0,
  onTabClick,
  children,
  tabsWrapperClassName,
  tabMenuClassName,
  tabContentClassName,
}: TabsProps) => {
  const childrenArray = useMemo(() => (Array.isArray(children) ? children : [children]), [children]);

  if (defaultTabIndex >= childrenArray.length) {
    console.warn(`defaultTabIndex (${defaultTabIndex}) is greater than number of tabs (${childrenArray.length})`);
  }

  const everyChildIsDisabled = childrenArray.every(({ props }) => props.disabled);
  if (everyChildIsDisabled) {
    console.warn("All tabs are disabled, please enable at least one tab");
  }

  // defaultTab isn't pointing to a disabled tab
  const firstEnabledTabIndex = childrenArray.findIndex(({ props }) => !props.disabled);
  const safeDefaultTabIndex = childrenArray[defaultTabIndex]?.props.disabled ? firstEnabledTabIndex : defaultTabIndex;

  const [activeTabIndex, setActiveTabIndex] = useState(safeDefaultTabIndex);

  const changeTab = useCallback(
    (newIndex: number) => {
      if (childrenArray[newIndex]?.props.disabled) return;

      onTabClick?.(newIndex);
      setActiveTabIndex(newIndex);
    },
    [childrenArray, onTabClick]
  );

  return (
    <div className={classNames(styles.wrapper, tabsWrapperClassName)}>
      <div className={classNames(styles.tabMenu, tabMenuClassName)}>
        {childrenArray.map(({ props }, index) => {
          const isDisabled = props.disabled;
          const isActive = activeTabIndex === index;

          return (
            <TabSelector
              key={`tabLabel-${index}`}
              onClick={() => changeTab(index)}
              active={isActive}
              disabled={isDisabled}
              role="tab"
              aria-selected={isActive}
              aria-controls={`tabContent-${index}`}
            >
              {props.label}
            </TabSelector>
          );
        })}
      </div>
      <div className={classNames(styles.tabView, tabContentClassName)}>{childrenArray[activeTabIndex]}</div>
    </div>
  );
};
